/**
 * Coordinates in-app notification sounds and toasts to prevent alert storms.
 *
 * - Sound and toast fire together when a batched alert is flushed
 * - Sound cooldown: at most one chime every SOUND_COOLDOWN_MS
 * - Toast batching: debounced summary instead of one toast per event
 * - Catch-up quiet period: no sounds/toasts for CATCH_UP_QUIET_MS after login
 */

import type { NotificationItem } from '../../services/api/notifications'
import { playSoftNotificationSound } from '../../utils/notificationSound'
import { toast } from '../../utils/toast'

const SOUND_COOLDOWN_MS = 5000
const TOAST_BATCH_MS = 400
const CATCH_UP_QUIET_MS = 30000

const CATEGORY_LABELS: Record<string, string> = {
  workflow_run: 'workflow run',
  cwb_complete: 'Workflow Builder run',
  aba_complete: 'Agent Builder run',
  agent_response: 'agent response',
  access_decision: 'access decision',
  user_registration: 'user registration',
  benchmark_assignment: 'benchmark assignment',
  knowledge_config: 'knowledge configuration',
  customer_analytics_digest: 'customer analytics digest',
  customer_analytics_alert: 'customer analytics alert',
}

function formatCategoryLabel(category: string, count: number): string {
  const label = CATEGORY_LABELS[category] ?? 'notification'
  if (count === 1) return `1 new ${label}`
  if (label.endsWith(' run')) return `${count} new ${label}s`
  return `${count} new ${label}s`
}

function singleToastMessage(item: NotificationItem): string {
  const title = item.title?.trim() || 'New notification'
  const body = item.body?.trim()
  if (!body || body === title) return title
  return `${title} — ${body}`
}

function singleToastType(item: NotificationItem): 'success' | 'info' | 'error' | 'warning' {
  const title = item.title?.toLowerCase() ?? ''
  if (title.includes('rejected')) return 'warning'
  if (item.category === 'agent_response' || title.includes('completed')) return 'success'
  if (title.includes('failed')) return 'error'
  return 'info'
}

function buildBatchToastMessage(items: NotificationItem[]): string {
  if (items.length === 1) {
    return items[0].title ?? 'New notification'
  }

  const byCategory = new Map<string, number>()
  for (const item of items) {
    const key = item.category || 'other'
    byCategory.set(key, (byCategory.get(key) ?? 0) + 1)
  }

  if (byCategory.size === 1) {
    const [category, count] = [...byCategory.entries()][0]
    return formatCategoryLabel(category, count)
  }

  return `${items.length} new notifications`
}

export interface NotificationAlertOptions {
  soundEnabled: boolean
  showToast: boolean
}

export class NotificationAlertCoordinator {
  private lastSoundAt = 0

  private catchUpUntil = 0

  private toastBatch: NotificationItem[] = []

  private batchWantsSound = false

  private toastTimer: ReturnType<typeof setTimeout> | null = null

  private openInbox: (() => void) | null = null

  private navigate: ((path: string) => void) | null = null

  setHandlers(handlers: { openInbox: () => void; navigate: (path: string) => void }): void {
    this.openInbox = handlers.openInbox
    this.navigate = handlers.navigate
  }

  beginCatchUpQuietPeriod(): void {
    this.catchUpUntil = Date.now() + CATCH_UP_QUIET_MS
    this.clearToastBatch()
  }

  reset(): void {
    this.lastSoundAt = 0
    this.catchUpUntil = 0
    this.clearToastBatch()
  }

  private inCatchUpQuietPeriod(): boolean {
    return Date.now() < this.catchUpUntil
  }

  private clearToastBatch(): void {
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
    this.toastBatch = []
    this.batchWantsSound = false
  }

  /**
   * Queue a notification alert. Sound and toast are delivered together when the
   * batch flushes so they stay in sync. Sound-only alerts play immediately.
   */
  notify(item: NotificationItem, options: NotificationAlertOptions): void {
    if (this.inCatchUpQuietPeriod()) return

    if (options.showToast && item.title) {
      this.toastBatch.push(item)
      if (options.soundEnabled) {
        this.batchWantsSound = true
      }
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => this.flushToasts(), TOAST_BATCH_MS)
      return
    }

    if (options.soundEnabled) {
      this.playSoundIfAllowed()
    }
  }

  private playSoundIfAllowed(onComplete?: () => void): void {
    const now = Date.now()
    if (now - this.lastSoundAt < SOUND_COOLDOWN_MS) {
      onComplete?.()
      return
    }
    this.lastSoundAt = now
    playSoftNotificationSound(onComplete)
  }

  private flushToasts(): void {
    const batch = this.toastBatch
    const wantsSound = this.batchWantsSound
    this.toastBatch = []
    this.batchWantsSound = false
    this.toastTimer = null
    if (batch.length === 0) return

    const showToasts = () => {
      const openInbox = () => this.openInbox?.()
      const navigate = this.navigate

      if (batch.length === 1) {
        const item = batch[0]
        const toastType = singleToastType(item)

        const onClick =
          item.deep_link && navigate ? () => navigate(item.deep_link as string) : openInbox
        toast.showToast(toastType, singleToastMessage(item), 8000, onClick)
        return
      }

      const allSuccess = batch.every(
        (item) => item.category === 'agent_response' || (item.title && item.title.toLowerCase().includes('completed'))
      )
      const anyFailed = batch.some((item) => item.title && item.title.toLowerCase().includes('failed'))

      let batchToastType: 'success' | 'info' | 'error' | 'warning' = 'info'
      if (anyFailed) {
        batchToastType = 'error'
      } else if (allSuccess) {
        batchToastType = 'success'
      }

      toast.showToast(batchToastType, buildBatchToastMessage(batch), 8000, openInbox)
    }

    if (wantsSound) {
      this.playSoundIfAllowed(showToasts)
      return
    }

    showToasts()
  }
}

export const notificationAlertCoordinator = new NotificationAlertCoordinator()
